import { notFound, redirect } from "@tanstack/react-router";
import { curatedCoworldName } from "@softmax/web-features/coworlds";
import { leagueRoute } from "@softmax/web-features/leagues";
import { buildHref, matchRoute } from "@softmax/web-addresses";
import { requireGameContext } from "@/features/game/data";
import { getActualViewer } from "@/data/viewer";
import { resolveGameLeague } from "@/features/game/data";
import { addressesOf } from "@/features/preview/state";
import { submitFormFor, type SubmitFormSpec } from "./forms";

export type SubmitPageData = {
  readonly viewerId: string;
  readonly canonicalHref: string;
  readonly displayName: string;
  readonly leagueId: string;
  readonly leagueName: string;
  readonly spec: SubmitFormSpec;
  readonly playHref: string;
  readonly startHref: string;
};

export async function readSubmitPage({
  signal,
  href,
}: {
  signal?: AbortSignal | undefined;
  href: string;
}) {
  const url = new URL(href, window.location.href);

  if (matchRoute(url.pathname).name !== "game.submit") throw notFound();

  const context = await requireGameContext({ signal, href });
  const params: { coworld: string; league?: string } = {
    coworld: context.address.slug,
  };

  if (context.league?.requestedSlug)
    params.league = context.league.requestedSlug;

  const address = addressesOf({ params, context });
  const [resolved, viewer] = await Promise.all([
    resolveGameLeague({ signal, publicAddress: address.url, context }),
    getActualViewer({ signal }),
  ]);

  if (resolved.status === "unavailable")
    throw new Error("This league is unavailable. Please retry.");

  if (resolved.status !== "ok") throw notFound();

  const league = resolved.league;
  const coworldName = league.game.coworld_name ?? league.game.name;
  const spec = submitFormFor(coworldName);

  if (!spec) throw notFound();

  const route = leagueRoute(league);
  const startHref = buildHref({ name: "game.start", ...route });

  if (!viewer?.subject_id) throw redirect({ href: startHref });

  return {
    viewerId: viewer.subject_id,
    canonicalHref: buildHref({ name: "game.submit", ...route }),
    displayName: curatedCoworldName(coworldName) ?? league.game.name,
    leagueId: league.id,
    leagueName: league.name,
    spec,
    playHref: buildHref({ name: "game.play", ...route }),
    startHref,
  };
}
